import { useParams, Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import KennelSEO from "./KennelSEO";
import { articles, faq20, categories } from "./seoArticlesData";

const KennelArticle = () => {
  useOutletContext();
  const { slug } = useParams();
  const article = articles.find(a => a.slug === slug);
  const [openFaq, setOpenFaq] = useState(null);

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#F5F5F5] text-lg font-serif mb-4">Artykuł nie znaleziony</p>
          <Link to="/wiedza-o-rasie" className="text-[#D4AF37] text-sm hover:underline">Wróć do bazy wiedzy</Link>
        </div>
      </div>
    );
  }

  const currentIdx = articles.findIndex(a => a.slug === slug);
  const prevArticle = currentIdx > 0 ? articles[currentIdx - 1] : null;
  const nextArticle = currentIdx < articles.length - 1 ? articles[currentIdx + 1] : null;
  const related = articles.filter(a => a.category === article.category && a.slug !== slug).slice(0, 3);
  const paragraphs = article.content.split("\n\n").filter(Boolean);

  return (
    <div>
      <KennelSEO
        title={article.metaTitle}
        description={article.metaDesc}
        canonical={`https://wolfevomkatzenberg.pl/wiedza-o-rasie/${article.slug}`}
      />

      {/* Breadcrumb */}
      <div className="bg-[#141416] border-b border-[#2A2A2D] px-6 md:px-12 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-2 text-[10px] tracking-wider text-[#B8B8B8]/40">
          <Link to="/" className="hover:text-[#D4AF37] transition-colors">START</Link>
          <span>/</span>
          <Link to="/wiedza-o-rasie" className="hover:text-[#D4AF37] transition-colors">WIEDZA O RASIE</Link>
          <span>/</span>
          <span className="text-[#D4AF37]/60">{categories[article.category]?.toUpperCase()}</span>
        </div>
      </div>

      {/* Article Header */}
      <section className="bg-[#141416] px-6 md:px-12 pt-12 pb-16">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/wiedza-o-rasie" className="text-xs tracking-wider text-[#B8B8B8]/40 hover:text-[#D4AF37] transition-colors flex items-center gap-2 mb-8" data-testid="back-to-knowledge">
              <ArrowLeft className="w-4 h-4" /> Wróć do bazy wiedzy
            </Link>
            <span className="text-[9px] tracking-[0.2em] text-[#D4AF37]/60 uppercase block mb-4">
              {categories[article.category] || article.category}
            </span>
            <h1 className="font-serif text-[clamp(1.8rem,3.5vw,3rem)] text-[#F5F5F5] leading-tight mb-6" data-testid="article-title">
              {article.title}
            </h1>
            <div className="w-16 h-px bg-[#D4AF37]/30" />
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-6 md:px-12 py-12 bg-[#0B0B0D]">
        <div className="max-w-4xl mx-auto">
          <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="space-y-6" data-testid="article-content"
          >
            {paragraphs.map((p, i) => (
              <p key={i} className="text-[#B8B8B8] text-sm leading-[1.9] md:text-base md:leading-[1.9]">{p}</p>
            ))}
          </motion.article>

          {/* FAQ Section (if article is FAQ) */}
          {article.isFaq && faq20.length > 0 && (
            <div className="mt-16" data-testid="faq-section">
              <h2 className="font-serif text-2xl text-[#F5F5F5] mb-8">Najczęściej zadawane pytania</h2>
              <div className="space-y-2">
                {faq20.map((item, i) => (
                  <div key={i} className="border border-[#2A2A2D] bg-[#1A1A1D]">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-5 text-left group"
                      data-testid={`faq-item-${i}`}
                    >
                      <span className="text-[#F5F5F5] text-sm pr-4 group-hover:text-[#D4AF37] transition-colors">{item.q}</span>
                      <ChevronDown className={`w-4 h-4 text-[#D4AF37]/50 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                    </button>
                    {openFaq === i && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                        className="px-5 pb-5 border-t border-[#2A2A2D]"
                      >
                        <p className="text-[#B8B8B8]/70 text-sm leading-relaxed pt-4">{item.a}</p>
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Article Links */}
          {article.links?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-[#2A2A2D]">
              <div className="flex flex-wrap gap-3">
                {article.links.map((link, i) => (
                  <Link key={i} to={link.url}
                    className="px-6 py-2.5 border border-[#D4AF37]/20 text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300"
                  >{link.text.toUpperCase()}</Link>
                ))}
              </div>
            </div>
          )}

          {/* Prev/Next Navigation */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
            {prevArticle && (
              <Link to={`/wiedza-o-rasie/${prevArticle.slug}`}
                className="bg-[#1A1A1D] border border-[#2A2A2D] hover:border-[#D4AF37]/20 p-5 group transition-all"
                data-testid="prev-article"
              >
                <span className="text-[9px] tracking-wider text-[#B8B8B8]/30 flex items-center gap-1 mb-2"><ArrowLeft className="w-3 h-3" /> POPRZEDNI</span>
                <p className="text-[#F5F5F5] text-sm group-hover:text-[#D4AF37] transition-colors line-clamp-2">{prevArticle.title}</p>
              </Link>
            )}
            {nextArticle && (
              <Link to={`/wiedza-o-rasie/${nextArticle.slug}`}
                className="bg-[#1A1A1D] border border-[#2A2A2D] hover:border-[#D4AF37]/20 p-5 group transition-all text-right md:col-start-2"
                data-testid="next-article"
              >
                <span className="text-[9px] tracking-wider text-[#B8B8B8]/30 flex items-center gap-1 justify-end mb-2">NASTĘPNY <ArrowRight className="w-3 h-3" /></span>
                <p className="text-[#F5F5F5] text-sm group-hover:text-[#D4AF37] transition-colors line-clamp-2">{nextArticle.title}</p>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="bg-[#141416] px-6 md:px-12 py-16">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-[10px] tracking-[0.3em] text-[#D4AF37] mb-8">POWIĄZANE ARTYKUŁY</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map(r => (
                <Link key={r.slug} to={`/wiedza-o-rasie/${r.slug}`}
                  className="bg-[#1A1A1D] border border-[#2A2A2D] hover:border-[#D4AF37]/20 p-6 group transition-all"
                  data-testid={`related-${r.slug}`}
                >
                  <span className="text-[9px] tracking-[0.2em] text-[#D4AF37]/40 uppercase block mb-2">{categories[r.category]}</span>
                  <h4 className="text-[#F5F5F5] text-sm group-hover:text-[#D4AF37] transition-colors leading-tight">{r.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default KennelArticle;
